/**
 * Short two-tone notification chime, synthesized via Web Audio API so no
 * binary asset needs to be shipped/loaded. Lazily creates one AudioContext
 * and reuses it; silently no-ops if the browser blocks audio before any
 * user gesture has occurred on the page.
 */

let audioCtx = null;

const getAudioContext = () => {
  if (audioCtx) return audioCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  audioCtx = new Ctx();
  return audioCtx;
};

const playTone = (ctx, frequency, startTime, duration) => {
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(frequency, startTime);
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(0.15, startTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  oscillator.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start(startTime);
  oscillator.stop(startTime + duration);
};

export const playNotificationSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    const now = ctx.currentTime;
    playTone(ctx, 880, now, 0.12);
    playTone(ctx, 1175, now + 0.09, 0.15);
  } catch (err) {
    console.error("Notification sound failed:", err);
  }
};

/**
 * Distinct tri-tone chime for an @-mention of the viewer — audibly different
 * from the generic two-tone `playNotificationSound`, since a mention is
 * addressed to the viewer personally rather than to whoever is watching.
 */
export const playMentionSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
    const now = ctx.currentTime;
    playTone(ctx, 988, now, 0.1);
    playTone(ctx, 1319, now + 0.08, 0.1);
    playTone(ctx, 1568, now + 0.16, 0.16);
  } catch (err) {
    console.error("Mention sound failed:", err);
  }
};

/**
 * Create/resume the shared AudioContext from inside a user-gesture call
 * stack, which is the only place browsers' autoplay policy allows it.
 * Idempotent — once the context is running this is a no-op.
 */
const primeNotificationSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
  } catch (err) {
    // Priming is best-effort; playback keeps its own guards.
  }
};

let primerInstalled = false;

/**
 * Install one-time window listeners that prime the AudioContext on the
 * first user gesture (click/keypress), so notification chimes work for the
 * rest of the session. Guarded so React StrictMode's double effect run
 * doesn't install twice.
 * @returns {Function} Remove listeners (no-op after they have fired).
 */
export const installSoundPrimer = () => {
  if (primerInstalled) {
    return () => {};
  }
  primerInstalled = true;
  const onGesture = () => {
    primeNotificationSound();
    remove();
  };
  const remove = () => {
    window.removeEventListener("pointerdown", onGesture, true);
    window.removeEventListener("keydown", onGesture, true);
    // Allow re-install after teardown — StrictMode's dev double-mount calls
    // the cleanup and then mounts again; a sticky flag would leave the
    // second mount with no listeners and priming permanently disabled.
    primerInstalled = false;
  };
  window.addEventListener("pointerdown", onGesture, { capture: true, passive: true });
  window.addEventListener("keydown", onGesture, { capture: true, passive: true });
  return remove;
};
